import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Crown, UserPlus, Trash2, KeyRound, ShieldOff, ShieldCheck, Copy, Check, X,
} from "lucide-react";

/**
 * Co-Admins panel. Lists every account with role=admin.
 * The founder record (is_founder=true) is protected: cannot be deleted,
 * deactivated, or force-reset by anyone (backend also enforces this).
 */
export default function CoAdminsPanel({ currentUser }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", temp_password: "" });
  const [tempResult, setTempResult] = useState(null);
  const [resetFor, setResetFor] = useState(null);
  const [newResetPw, setNewResetPw] = useState("");

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/admin/co-admins");
      setList(data || []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/co-admins", form);
      toast.success("Co-Admin created");
      setTempResult(data);
      setForm({ name: "", email: "", phone: "", temp_password: "" });
      setShowCreate(false);
      await load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const doDelete = async (row) => {
    if (!window.confirm(`Delete co-admin ${row.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/co-admins/${row.id}`);
      toast.success("Co-Admin removed");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const toggleActive = async (row) => {
    try {
      await api.patch(`/admin/co-admins/${row.id}`, { active: !row.active });
      toast.success(row.active ? "Deactivated" : "Reactivated");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const doReset = async () => {
    if (!resetFor || newResetPw.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    try {
      await api.patch(`/admin/co-admins/${resetFor.id}`, { reset_password: newResetPw });
      toast.success("Password reset");
      setTempResult({ name: resetFor.name, email: resetFor.email, temp_password: newResetPw });
      setResetFor(null);
      setNewResetPw("");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="mb-10" data-testid="co-admins-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-1">
            // Owners · full-access admins
          </div>
          <h2 className="font-display text-2xl font-black uppercase tracking-tighter">
            Co-Admins
          </h2>
          <p className="text-xs text-white/50 mt-1 max-w-xl">
            Give equal-parity access to a partner or co-founder. Co-admins can do everything you can —
            except delete or demote the <b className="brand-orange">Founder</b> account.
          </p>
        </div>
        <button
          onClick={() => setShowCreate((s) => !s)}
          data-testid="co-admin-add-btn"
          className="flex items-center gap-2 bg-brand-orange text-white px-4 py-2 font-bold text-xs uppercase tracking-widest hover:opacity-90"
        >
          <UserPlus size={14} /> {showCreate ? "Cancel" : "Add Co-Admin"}
        </button>
      </div>

      {tempResult && (
        <TempPwCard data={tempResult} onClose={() => setTempResult(null)} />
      )}

      {showCreate && (
        <form
          onSubmit={create}
          className="border border-brand-orange/40 bg-brand-orange/5 p-5 mb-4 grid grid-cols-1 md:grid-cols-2 gap-3"
          data-testid="co-admin-create-form"
        >
          <input
            value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            required placeholder="Full name" data-testid="co-admin-name"
            className="bg-transparent border border-white/20 py-2.5 px-3 focus:outline-none focus:border-brand-orange"
          />
          <input
            type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            required placeholder="Email" data-testid="co-admin-email"
            className="bg-transparent border border-white/20 py-2.5 px-3 focus:outline-none focus:border-brand-orange"
          />
          <input
            value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder="Phone (optional)" data-testid="co-admin-phone"
            className="bg-transparent border border-white/20 py-2.5 px-3 focus:outline-none focus:border-brand-orange"
          />
          <input
            value={form.temp_password} onChange={(e) => setForm({ ...form, temp_password: e.target.value })}
            required minLength={6} placeholder="Temporary password (min 6)" data-testid="co-admin-temppw"
            className="bg-transparent border border-white/20 py-2.5 px-3 focus:outline-none focus:border-brand-orange"
          />
          <button
            type="submit" data-testid="co-admin-submit"
            className="md:col-span-2 bg-brand-orange text-white py-3 font-bold text-sm uppercase tracking-widest hover:opacity-90"
          >
            Create Co-Admin
          </button>
        </form>
      )}

      {resetFor && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4" data-testid="co-admin-reset-modal">
          <div className="bg-[#131A2B] border border-white/12 p-6 max-w-md w-full">
            <div className="font-bold uppercase mb-1">Reset password</div>
            <div className="text-sm text-white/60 mb-4">for {resetFor.name} ({resetFor.email})</div>
            <input
              autoFocus type="text" value={newResetPw} onChange={(e) => setNewResetPw(e.target.value)}
              placeholder="New temporary password (min 6)"
              data-testid="co-admin-reset-newpw"
              className="w-full bg-transparent border border-white/20 py-3 px-4 mb-4 focus:outline-none focus:border-brand-orange"
            />
            <div className="flex gap-2">
              <button
                onClick={doReset} data-testid="co-admin-reset-submit"
                className="flex-1 bg-brand-orange text-white py-3 font-bold text-sm uppercase tracking-widest"
              >Reset</button>
              <button
                onClick={() => { setResetFor(null); setNewResetPw(""); }}
                className="px-4 border border-white/20 text-sm"
              >Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {loading && <div className="text-white/40 py-4">Loading…</div>}
        {!loading && list.map((row) => {
          const isFounder = !!row.is_founder;
          const isSelf = row.id === currentUser?.id;
          return (
            <div
              key={row.id}
              className={`border p-5 flex flex-wrap items-center gap-4 justify-between ${
                isFounder ? "border-brand-orange/60 bg-brand-orange/5" : "border-white/12 bg-[#131A2B]"
              }`}
              data-testid={`co-admin-row-${row.id}`}
            >
              <div className="flex items-center gap-4 min-w-[240px] flex-1">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  isFounder ? "bg-brand-orange/20" : "bg-brand-blue/20"
                }`}>
                  {isFounder
                    ? <Crown size={18} className="brand-orange" />
                    : <ShieldCheck size={18} className="brand-blue" />}
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold">{row.name}</span>
                    {isFounder && (
                      <span className="text-[10px] font-mono uppercase tracking-widest bg-brand-orange text-white px-2 py-0.5">
                        Founder
                      </span>
                    )}
                    {!isFounder && (
                      <span className="text-[10px] font-mono uppercase tracking-widest bg-brand-blue text-white px-2 py-0.5">
                        Co-Admin
                      </span>
                    )}
                    {isSelf && (
                      <span className="text-[10px] font-mono uppercase tracking-widest border border-white/30 px-2 py-0.5 text-white/70">
                        You
                      </span>
                    )}
                    {!row.active && (
                      <span className="text-[10px] font-mono uppercase tracking-widest text-red-400">
                        Deactivated
                      </span>
                    )}
                    {row.must_change_password && (
                      <span className="text-[10px] font-mono uppercase tracking-widest brand-orange">
                        PW change pending
                      </span>
                    )}
                  </div>
                  <div className="text-xs font-mono text-white/50 mt-1">{row.email}</div>
                  {row.phone && <div className="text-xs text-white/40">{row.phone}</div>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {/* Actions — hidden for founder; also hidden for self except password reset (self can use profile) */}
                {!isFounder && (
                  <>
                    <button
                      onClick={() => setResetFor({ id: row.id, name: row.name, email: row.email })}
                      data-testid={`co-admin-resetpw-${row.id}`}
                      className="text-xs font-mono uppercase tracking-widest border border-white/20 px-3 py-1 hover:bg-white/10 flex items-center gap-1"
                    >
                      <KeyRound size={12} /> Reset PW
                    </button>
                    {!isSelf && (
                      <button
                        onClick={() => toggleActive(row)}
                        data-testid={`co-admin-toggle-${row.id}`}
                        className="text-xs font-mono uppercase tracking-widest border border-white/20 px-3 py-1 hover:bg-white/10 flex items-center gap-1"
                      >
                        {row.active
                          ? <><ShieldOff size={12} /> Deactivate</>
                          : <><ShieldCheck size={12} /> Reactivate</>}
                      </button>
                    )}
                    {!isSelf && (
                      <button
                        onClick={() => doDelete(row)}
                        data-testid={`co-admin-delete-${row.id}`}
                        className="text-xs font-mono uppercase tracking-widest border border-red-500/40 text-red-400 px-3 py-1 hover:bg-red-500/10 flex items-center gap-1"
                      >
                        <Trash2 size={12} /> Remove
                      </button>
                    )}
                  </>
                )}
                {isFounder && (
                  <span className="text-[10px] font-mono uppercase tracking-widest text-white/40">
                    Locked · Founder-only actions
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TempPwCard({ data, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(
      `HTP Sports Hub admin login\nEmail: ${data.email}\nTemporary password: ${data.temp_password}\nPlease log in and change it on first sign-in.`
    );
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="mb-4 border-2 border-brand-orange bg-brand-orange/10 p-5" data-testid="co-admin-temp-card">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="font-mono text-[10px] uppercase tracking-widest brand-orange mb-1">
            // Share once · temporary co-admin password
          </div>
          <div className="font-bold text-lg">{data.name}</div>
          <div className="text-sm text-white/70">{data.email}</div>
          <div className="mt-3 font-mono text-2xl brand-blue tracking-wider select-all">
            {data.temp_password}
          </div>
          <div className="text-xs text-white/50 mt-2">
            This password is displayed <b>once</b>. Share it securely; the co-admin must change it on first login.
          </div>
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <button
            onClick={copy} data-testid="co-admin-temp-copy"
            className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest border border-brand-blue text-brand-blue px-3 py-2 hover:bg-brand-blue hover:text-white"
          >
            {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
          </button>
          <button
            onClick={onClose} data-testid="co-admin-temp-close"
            className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest border border-white/20 px-3 py-2 hover:bg-white/10"
          >
            <X size={14} /> Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}
